/* @flow */

import type { Level, Content } from "@crossroads/shop-state/messages";

import React, { useState, useEffect, useCallback, useRef } from "react";
import styles from "./styles.scss";
import cn from "classnames";
import { useTranslate } from "@awardit/react-use-translate";
import Wrapper from "components/Wrapper";
import { removeMessage } from "@crossroads/shop-state/messages";
import { MessagesData } from "data";
import { useData, useSendMessage } from "crustate/react";
import { Button, Foldable } from "@crossroads/ui-components";
import RemoveIcon from "icons/cross.svg";

type MessageProps = {
  content: Content,
  level: Level,
  onRemove: () => void,
};

const MESSAGE_TRANSITION = 400;
const LIFETIME = 10000;

const Message = ({ content, level, onRemove }: MessageProps) => {
  const t = useTranslate();
  const [deleting, setDeleting] = useState(false);
  const ref = useRef();

  const text = typeof content === "string" ?
    t("STATUS_CODE." + content) :
    t("STATUS_CODE." + content.translationKey, content.variable);

  const close = useCallback(() => {
    if (deleting) {
      return;
    }

    setDeleting(true);

    setTimeout(() => {
      if (deleting) {
        setDeleting(false);
        onRemove();
      }
    }, MESSAGE_TRANSITION);
  }, [onRemove, deleting]);

  useEffect(() => {
    const element = ref.current;

    if (element) {
      // Select the first focusable element inside the container
      const focusable = element.querySelector(
        "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])"
      );

      if (focusable instanceof HTMLElement) {
        focusable.focus();
      }
    }

    setTimeout(() => {
      close();
    }, LIFETIME);
  }, [close]);

  return (
    <Foldable
      role="alert"
      open={!deleting}
      className={cn(
        styles.message,
        styles[level],
        { [styles.deleting]: deleting }
      )}
      onClick={close}
    >
      <div className={styles.itemInner}>
        <div ref={ref} className={styles.itemPadding}>
          <Button
            type="button"
            aria-label={t("CLOSE_SYSTEM_MESSAGE")}
            className={styles.close}
            onClick={close}
          >
            <RemoveIcon
              aria-label={t("ICONS.CROSS")}
              className={styles.icon}
            />
          </Button>
          <p>
            {text}
          </p>
        </div>
      </div>
    </Foldable>
  );
};

const SystemMessages = (): React$Node => {
  const sendMessage = useSendMessage();
  const messages = useData(MessagesData);

  return (
    <div className={styles.block}>
      <Wrapper className={styles.wrapper}>
        <div className={styles.messages}>
          {messages.map(x => (
            <Message
              {...x}
              key={x.id}
              onRemove={() => {
                if (x.id !== undefined) {
                  sendMessage(removeMessage(x.id));
                }
              }}
            />
          ))}
        </div>
      </Wrapper>
    </div>
  );
};

export default SystemMessages;
